class FiabScrollAccordion extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.track = this.querySelector('[data-scroll-accordion-track]');
    this.items = Array.from(this.querySelectorAll('[data-scroll-accordion-item]'));
    this.images = Array.from(this.querySelectorAll('[data-scroll-accordion-image]'));
    this.desktopMedia = window.matchMedia('(min-width: 768px)');
    this.reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.activeIndex = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-open')));
    this.ticking = false;

    if (!this.track || !this.items.length) return;

    this.onScroll = this.handleScroll.bind(this);
    this.onViewportChange = this.sync.bind(this);
    this.onBlockSelect = this.handleBlockSelect.bind(this);
    this.buttonHandlers = [];

    this.items.forEach((item, index) => {
      const button = item.querySelector('[data-scroll-accordion-button]');
      if (!button) return;
      const handler = () => this.handleButtonClick(index);
      button.addEventListener('click', handler);
      this.buttonHandlers.push({ button, handler });
    });

    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onViewportChange);
    this.desktopMedia.addEventListener('change', this.onViewportChange);
    this.reducedMotionMedia.addEventListener('change', this.onViewportChange);
    this.addEventListener('shopify:block:select', this.onBlockSelect);
    this.sync();
  }

  disconnectedCallback() {
    if (!this.initialized) return;
    window.removeEventListener('scroll', this.onScroll);
    window.removeEventListener('resize', this.onViewportChange);
    this.desktopMedia?.removeEventListener('change', this.onViewportChange);
    this.reducedMotionMedia?.removeEventListener('change', this.onViewportChange);
    this.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.buttonHandlers?.forEach(({ button, handler }) => button.removeEventListener('click', handler));
    this.initialized = false;
  }

  isPinned() {
    return this.dataset.pinDesktop === 'true' && this.desktopMedia.matches && !this.reducedMotionMedia.matches;
  }

  setActive(index) {
    this.activeIndex = index;
    this.items.forEach((item, itemIndex) => {
      const open = itemIndex === index;
      item.classList.toggle('is-open', open);
      item.querySelector('[data-scroll-accordion-button]')?.setAttribute('aria-expanded', open ? 'true' : 'false');
    });

    if (index >= 0) {
      this.images.forEach((image, imageIndex) => image.classList.toggle('is-active', imageIndex === index));
    }
  }

  bandHeight() {
    return (this.track.offsetHeight - window.innerHeight) / this.items.length;
  }

  indexFromScroll() {
    const distance = this.track.offsetHeight - window.innerHeight;
    if (distance <= 0) return 0;
    const progress = -this.track.getBoundingClientRect().top / distance;
    return Math.max(0, Math.min(this.items.length - 1, Math.floor(progress * this.items.length)));
  }

  handleScroll() {
    if (this.ticking || !this.isPinned()) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      if (this.isPinned()) this.setActive(this.indexFromScroll());
    });
  }

  handleButtonClick(index) {
    if (this.isPinned()) {
      const top = window.scrollY + this.track.getBoundingClientRect().top + (index + 0.5) * this.bandHeight();
      window.scrollTo({ top, behavior: 'smooth' });
      return;
    }
    this.setActive(this.activeIndex === index ? -1 : index);
  }

  handleBlockSelect(event) {
    const item = event.target.closest('[data-scroll-accordion-item]');
    const index = this.items.indexOf(item);
    if (index >= 0) this.setActive(index);
  }

  sync() {
    this.setActive(this.isPinned() ? this.indexFromScroll() : this.activeIndex);
  }
}

if (!customElements.get('fiab-scroll-accordion')) {
  customElements.define('fiab-scroll-accordion', FiabScrollAccordion);
}
